/* eslint-disable @typescript-eslint/no-unused-vars */
import { InputWithLabel } from '@adotel/components'
import { useQuery, useMutation } from '@apollo/client'
import { Button, Typography, useMediaQuery, useTheme } from '@material-ui/core'
import SearchIcon from '@material-ui/icons/Search'
import { matchSorter } from 'match-sorter'
import { useEffect, useState } from 'react'
import { useParams } from 'react-router-dom'
import { toast } from 'react-toastify'

import { MODIFY_ROOMTYPE_FACILITIES_MUTATION } from '../../graphql/modifyRoomTypeFacilities'
import { ROOMTYPE_FACILITIES_QUERY } from '../../graphql/roomTypeFacilities'
import { getRoleFromStorage } from '../../helpers/userData'
import { getTranslate } from '../../localization'
import roomTypePagesRoleManagement from '../../staticRoleManagement/roomTypeRoleManagement'
import { FacilityGroup } from '../../types/accommodationFacilities'
import FacilitiesCategory from '../AccommodationFacilities/FacilitiesCategory'
import useStyles from '../AccommodationFacilities/styles/index.styles'
import FacilitiesCategorySkeleton from './FacilitiesCategory.skeleton'

const RoomTypeFacilities = () => {
  const theme = useTheme()
  const classes = useStyles()
  const isSmallScreen = useMediaQuery(theme.breakpoints.down('sm'))
  const [load, setLoad] = useState(false)

  const [filteredRoomTypeFacilities, setFilteredRoomTypeFacilities] = useState<
    FacilityGroup[]
  >([])
  const [searchInputValue, setSearchInputValue] = useState<string>('')
  const [selectedIDs, setSelectedIDs] = useState<string[]>([])
  const params = useParams<{
    roomTypeCode: string
    propertyCode: string
  }>()

  const { data, loading } = useQuery<{
    getRoomTypeFacilities: FacilityGroup[]
  }>(ROOMTYPE_FACILITIES_QUERY, {
    variables: {
      propertyCode: params.propertyCode,
      roomTypeCode: params.roomTypeCode
    }
  })

  const [mutateFunction] = useMutation(MODIFY_ROOMTYPE_FACILITIES_MUTATION)
  const onSave = () => {
    setLoad(true)
    mutateFunction({
      variables: {
        selectedIDs: selectedIDs,
        roomTypeCode: params.roomTypeCode,
        propertyCode: params.propertyCode
      }
    })
      .then(({ data: response }) => {
        setLoad(false)
        toast.success(response.saveRoomTypeFacilities.message)
      })
      .catch((response) => toast.error(response.message))
  }

  useEffect(() => {
    if (data) setFilteredRoomTypeFacilities(data?.getRoomTypeFacilities)
  }, [data])

  // --------- when searchInput changes ------------
  useEffect(() => {
    if (data) {
      let newRoomTypeFacilities = data.getRoomTypeFacilities

      if (searchInputValue) {
        newRoomTypeFacilities = newRoomTypeFacilities
          .map((facilityGroup) => ({
            label: facilityGroup.label,
            facilities: matchSorter(
              facilityGroup.facilities,
              searchInputValue,
              {
                keys: ['label']
              }
            )
          }))
          .filter((facilityGroup) => facilityGroup.facilities.length > 0)
      }

      setFilteredRoomTypeFacilities([...newRoomTypeFacilities])
    }
  }, [searchInputValue])
  // --------- end when searchInput changes ------------

  const hasAccessToWriteRoomTypeFacilities = roomTypePagesRoleManagement
    .find(
      (item) =>
        item.route ===
        '/accommodations/:accommodationID/:propertyCode/room-types/:roomTypeId/:roomTypeCode/:page'
    )
    ?.tabs?.find((tabItem) => tabItem.params.page === 'facilities')
    ?.rolesListWithWriteAccess?.includes(getRoleFromStorage())

  return (
    <div className={classes.root}>
      <div className={classes.header}>
        <Typography variant="h3" gutterBottom>
          {getTranslate(isSmallScreen ? 'Facilities' : 'Room Type Facilities')}
        </Typography>
      </div>
      <div className={classes.searchInputContainer}>
        <InputWithLabel
          value={searchInputValue}
          onChange={(e) => setSearchInputValue(e.currentTarget.value)}
          placeholder={getTranslate('Search by name')}
          autoComplete="off"
          noHelperText
          fullWidth
          startIcon={<SearchIcon />}
          className={classes.searchInput}
        />
      </div>
      <div className={classes.facilitiesListContainer}>
        {loading &&
          [...Array(3)].map((_, index) => (
            <FacilitiesCategorySkeleton key={index.toString()} />
          ))}
        {!loading &&
          filteredRoomTypeFacilities.map(
            (facilityGroupItem, facilityGroupItemIndex) => (
              <FacilitiesCategory
                key={facilityGroupItem.label}
                title={facilityGroupItem.label}
                facilities={facilityGroupItem.facilities}
                selectedIDs={selectedIDs}
                setSelectedIDs={setSelectedIDs}
              />
            )
          )}
      </div>
      <div className={classes.button}>
        <Button
          disabled={load || !hasAccessToWriteRoomTypeFacilities}
          variant="contained"
          color="primary"
          fullWidth={isSmallScreen}
          onClick={onSave}>
          {getTranslate('Save')}
        </Button>
      </div>
    </div>
  )
}

export default RoomTypeFacilities
